/**
 * useShellDomainSync - the shell's wire: the battlefield's documents, the
 * session sync (and what it says to the person at the screen), and the
 * table's house rules resolved once.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import { useState, useCallback, useMemo, useRef } from 'react';
import useBattlefieldSync, { groundAtFor } from './useBattlefieldSync.js';
import { terrainLiftText } from './useShellLiveMap';
import useShellSyncNotices from './useShellSyncNotices';
import useFirestoreSync from './useFirestoreSync';
import useHiddenFigures from './useHiddenFigures';
import useHiddenTokenRouting from './useHiddenTokenRouting';
import useVeil from './useVeil';
import { useToast } from '../contexts/ToastContext';
import useShellHouseRules from './useShellHouseRules';
import useDirection from './useDirection';
import useKillTeams from './useKillTeams';
import useMapFold from './useMapFold';
import useLivery from './useLivery';
import usePublishTableDefaults from './usePublishTableDefaults';
import useShellGround from './useShellGround';
import useAsks from './useAsks';
import useSceneUnaware from './useSceneUnaware';
import * as multiplayer from '../multiplayer';
import useCameraCut from './useCameraCut';
import useSettleOnBoxes from './useSettleOnBoxes';
import { objectsForSettle, settleOnMap } from '../engine/objects/settleOnMap';
import useRelationRules from './useRelationRules';

/**
 * The battlefield's documents, and whether the map wants the log's column.
 *
 * `s` MUST be the shell scope itself, not a copy: a terrain lift is logged
 * through `s.addNarrative`, which the table domain declares further down.
 * Read at call time, it is this render's writer - exactly what naming it
 * in the shell's single body used to capture. `hidden` is useHiddenFigures'.
 */
function useShellMapSync(s, hidden) {
  const { roster, activeTab, isGm, sessionId, mergePingsRef } = s;
  /**
   * Does the map want the log's column? Build and Deploy both do.
   *
   * STATE PLUS AN EFFECT, not an edge handler. Restoring on the way out
   * meant nothing restored when the way out was never announced - the
   * house rule switched off mid-Build, a mode button filtered away, a
   * remount. Saying the current truth on every render cannot miss an
   * edge, and the hook's yield is idempotent so repeating it is free.
   */
  const [mapWantsColumn, setMapWantsColumn] = useState(false);
  // How a map edit settles everyone: on the terrain and the Objects' boxes (2026-10-26). The roster and every token
  // are filled in below, once the hidden figures' tokens are merged, and read only when a map changes.
  const objectsRef = useRef(null);
  const settle = useCallback(
    (tokens, terrain, roster) =>
      settleOnMap(tokens, terrain, objectsForSettle(objectsRef.current, tokens, roster), groundAtFor(terrain)),
    [],
  );
  const battlefield = useBattlefieldSync({
    sessionId,
    isGm,
    settle,
    onLog: (ids, moves) => {
      const text = terrainLiftText(roster, moves);
      if (text) s.addNarrative(activeTab, { type: 'info', text, timestamp: Date.now() });
    },
    // Read through a ref: `usePings` runs in the map domain, after this,
    // so there is nothing to name yet. (The ref is stable, so listing it
    // never re-creates the callback.)
    onPings: useCallback((docs) => mergePingsRef.current(docs), [mergePingsRef]),
  });
  // The GM's map includes the hidden figures' tokens, kept in their own records - see the hook.
  const { tokens, writeToken, removeToken } = useHiddenTokenRouting(battlefield, hidden, settle);
  objectsRef.current = { roster, tokens, hiddenIds: hidden.hiddenIds };
  const { terrain, setTerrain, library, deployment, setDeployment, saveMap, deleteMap, importMaps, loadMap, copyMap } =
    battlefield;
  // A box that changes (destroyed, moved, resized) settles who stands on it - see the hook.
  useSettleOnBoxes({ isGm, terrain, roster, tokens, hiddenIds: hidden.hiddenIds, writeToken });
  // Each team's own arrival point (plan scene-editor-library, B), beside the main marker.
  const { arrivals, setArrivals } = battlefield;

  return {
    // The push takes departures off the SHARED map: a figure just hidden leaves the shared roster and
    // its shared token must go, while its hidden one stays (routing it would lift the hidden token).
    removeSharedToken: battlefield.removeToken,
    // The fuzz tap compares screens on the shared map, never the GM's hidden tokens.
    sharedTokens: battlefield.tokens,
    mapWantsColumn,
    setMapWantsColumn,
    terrain,
    setTerrain,
    tokens,
    writeToken,
    removeToken,
    library,
    deployment,
    setDeployment,
    arrivals,
    setArrivals,
    saveMap,
    deleteMap,
    importMaps,
    loadMap,
    copyMap,
  };
}

/**
 * The session sync, its notices and the expired-reaction late binding.
 * Length: the sync's argument record and the names it hands on (declarative).
 */
function useShellWire(s) {
  const {
    user,
    setSession,
    setConnectedPlayers,
    tgtTemplates,
    setTgtTemplates,
    actorId,
    hidden,
    veil,
    setTabs,
    setActiveTab,
    isGm,
    sessionId,
    playerId,
  } = s;
  // Refused writes, lost and regained connections, and a revoked seat -
  // what the sync layer says to the person at the screen. See the hook.
  const { pushToast } = useToast();
  const onSyncError = useShellSyncNotices(pushToast, setSession);

  // AN EXPIRED REACTION IS ANSWERED, NOT DELETED - see useDefenceSync.
  //
  // The sweep that notices the expiry lives in the sync layer, which runs
  // domains before the attack domain that knows how to resolve one. A ref
  // late-binds the two rather than reordering the hooks; the callback handed
  // to the sync layer is stable, so the sweep never restarts.
  const resolveExpiredDefenceRef = useRef(null);
  const onDefenceExpire = useCallback((combatantId, doc) => {
    resolveExpiredDefenceRef.current?.(combatantId, doc);
  }, []);

  const sync = useFirestoreSync({
    sessionId,
    isGm,
    playerId,
    user,
    actorId,
    // ROSTER tabs, not the live-cover ones: the sync writes what is
    // stored, and what the map is currently saying is not stored. Like the
    // roster, only the SHARED slice: a hidden figure never reaches the wire.
    tabs: hidden.sharedTabs,
    setTabs,
    roster: hidden.sharedRoster,
    setRoster: hidden.setSharedRoster,
    setActiveTab,
    // Loadouts belong to combatants now; the shared profiles doc is
    // ownerless and would let a remote grid land on my actor. B4 replaces
    // it with per-combatant sync.
    tgtTemplates,
    setTgtTemplates,
    setSession,
    setConnectedPlayers,
    onSyncError,
    onDefenceExpire,
    // What a player may read of a hidden figure's act: its alias (plan map-display, G1) - see useVeil.
    veil,
  });

  const {
    syncTargetToFirestore,
    syncAllTabsToFirestore,
    syncCombatantState,
    syncCombatantSheet,
    removeCombatant,
    forgetHeard,
    rosterHydrated,
    setTabSourceCharacter,
    syncNarrativeToFirestore,
    flushNarrativeQueue,
    firestoreNarrative,
    narrativeClearTime: _narrativeClearTime,
    setNarrativeClearTime,
    onlinePlayers,
    permissions,
    isLocked,
    createPendingDefence,
    clearPendingDefence: clearPendingDefenceFirestore,
    clearAllPendingDefences,
    clearPendingDefencesSince,
    getDefenceForTab,
    isDefencePending,
    pendingDefences,
  } = sync;

  // Routed like the tokens (plan map-display, G1): a hidden id has no shared document, so removing one is local.
  // The push gets the raw one - a figure just hidden must lose its shared document.
  const { isHidden } = hidden;
  // A shared copy of a hidden figure (a player's late write re-created it) is deleted again - see useHiddenFigures.
  hidden.strayRef.current = removeCombatant;
  const removeRouted = useCallback(
    (id) => (isHidden(id) ? undefined : removeCombatant(id)),
    [isHidden, removeCombatant],
  );

  return {
    pushToast,
    resolveExpiredDefenceRef,
    sync,
    syncTargetToFirestore,
    syncAllTabsToFirestore,
    syncCombatantState,
    syncCombatantSheet,
    removeCombatant: removeRouted,
    removeSharedCombatant: removeCombatant,
    forgetHeard,
    rosterHydrated,
    setTabSourceCharacter,
    syncNarrativeToFirestore,
    flushNarrativeQueue,
    firestoreNarrative,
    setNarrativeClearTime,
    onlinePlayers,
    permissions,
    isLocked,
    createPendingDefence,
    clearPendingDefenceFirestore,
    clearAllPendingDefences,
    clearPendingDefencesSince,
    getDefenceForTab,
    isDefencePending,
    pendingDefences,
  };
}

/**
 * The table's rulings, and loading a map under them. EVERY resolved ruling is handed on as one record: a relay that
 * named them one by one dropped `assistBonus` (until v11.92.1) and `coverReach` (2026-10-26) without a sound.
 */
function useShellRules(s) {
  const { isGm, sessionId, playerId, setLogYield, mapWantsColumn, terrain, loadMap, permissions } = s;
  // The table's rulings, resolved once - see `hooks/useShellHouseRules`.
  const rules = useShellHouseRules({ permissions, terrain, sessionId, playerId, isGm, mapWantsColumn, setLogYield });
  const { redeployOnMapLoad, avoidOnDeploy } = rules;

  /** Loading a map, with the deployment rules the table has set. */
  const loadMapWithRules = useCallback(
    (next) => loadMap(next, { redeployOnMapLoad, avoid: avoidOnDeploy }),
    [loadMap, redeployOnMapLoad, avoidOnDeploy],
  );

  // The rulings every relation reads, as one record - see the hook.
  const relationRules = useRelationRules(rules.coverReach, s.roster);
  return { ...rules, loadMapWithRules, relationRules };
}

/**
 * @param {object} s - the shell scope (see useShellMapSync: the scope itself).
 * @returns {object} the battlefield's documents and verbs, `hidden` (useHiddenFigures) and `sharedRoster`,
 *   the sync's record
 *   (and `sync` whole), `pushToast`, the house rules, `loadMapWithRules`, `direction`, `mapFold`, `danger`,
 *   `positionsCount`, `ruleTokens` and `asks`.
 */
export default function useShellDomainSync(s) {
  // The people only the GM can see, and the shared slice the sync layer is given instead - see the hook.
  const hidden = useHiddenFigures({ ...s, mp: multiplayer });
  const veil = useVeil(hidden.figuresRef, s.roster, s.actorId);
  const map = useShellMapSync(s, hidden);
  const wire = useShellWire({ ...s, ...map, hidden, veil });
  const rules = useShellRules({ ...s, ...map, ...wire });
  // The session phase (narrative, stealth, combat) and the GM's peek - see the hook.
  const direction = useDirection({ sessionId: s.sessionId, mp: multiplayer });
  // The kill-teams, read off the sheets, and their Cohesion (Squad Mode) - see the hook. The SHARED roster: a hidden
  // leader read on the GM's screen alone would give it another start than the players'.
  const killTeams = useKillTeams({ sessionId: s.sessionId ?? null, mp: multiplayer, roster: hidden.sharedRoster });
  // The kill-team's verbs, for the domains that run before they exist (the People domain fills it each render): a
  // REF, because the Attack and Table domains read a COPY of the scope, which a later Object.assign never reaches.
  const killTeamVerbs = useRef(null);
  // Narrative folds the map behind the log, per viewer, on every layout, unless Build or Deploy wants it - see the hook.
  const mapFold = useMapFold(direction.shown, map.mapWantsColumn);
  // The root wears the phase and the danger step; the reading says who stands against the party.
  const danger = useLivery(s.roster, direction);
  // The table's display defaults, for the hooks read too widely to take them as a prop (the motion).
  usePublishTableDefaults(wire.permissions?.houseRules);
  // Whether positions count, and the markers every RULE reads - see the hook.
  const ground = useShellGround(map.tokens, direction.frame);
  // One seat asking another (who an area attack caught, and later the calls for tests) - see the hook.
  const seat = useMemo(
    () => ({ uid: s.user?.uid ?? null, isGm: !!s.isGm, name: s.displayName }),
    [s.user?.uid, s.isGm, s.displayName],
  );
  const asks = useAsks({ sessionId: s.sessionId, mp: multiplayer, seat });
  // The GM's cut: every display camera to one figure, refused for a hidden one - see the hook.
  const cameraCut = useCameraCut({
    sessionId: s.sessionId,
    mp: multiplayer,
    isGm: !!s.isGm,
    isHidden: hidden.isHidden,
  });
  // Who Stealth's ladder says has not noticed, and the Easy (+30) it adds to the sum the attacker
  // domain made before the phase was known. REPLACES `effectiveCustomMods` and `attackMod` in the scope.
  // (The conditional effects are applied after the map, in the Map domain: they may ask the range.)
  const scene = useSceneUnaware({ ...s, direction });
  return {
    ...map,
    ...wire,
    ...rules,
    killTeams,
    killTeamVerbs,
    hidden,
    sharedRoster: hidden.sharedRoster,
    direction,
    cameraCut,
    mapFold,
    danger,
    ...ground,
    asks,
    ...scene,
  };
}
